/**
 * D-020 V2B — the A/D hotkey listener (LT2-014/015/016), with #609's
 * browser-interaction fence.
 *
 * Mounted once per hosting surface (ValueDashboard, GameDetail), gated by the
 * caller on lt2Access && lt2HotkeysEnabled. Semantics (packet §V2B; the
 * spoken source correction governs — NOT the handwritten exhibit):
 *
 *   Vertical team layout (moneyline): A = the visually TOP team's visible
 *     NO action, D = the visually BOTTOM team's visible NO action — focus is
 *     per GAME, and the two keys land on two different team markets, never a
 *     YES/NO pair within one market.
 *   Side-by-side two-sided ladder (spread/total): A = LEFT displayed
 *     column, D = RIGHT.
 *
 * Size = the visible one-click preset; price = EXACTLY the displayed
 * top-of-book action the focused card's resolver reports (the same value a
 * mouse click there would submit); the same submit-time guard applies via
 * `submitIntent` — identical path, identical `lt-` coid namespace.
 *
 * ── The fence (#609) ─────────────────────────────────────────────────────
 * This handler is the OUTERMOST of three fences, not the last one:
 * `evaluateIntentGuard` re-validates identity/availability/price/role against
 * the current book at submit time, and the relay enforces the LT2 grant and
 * the contract ceiling. What lives here is only the browser-interaction
 * question — "was this keystroke plausibly a deliberate order at a card the
 * trader is looking at?" — and it answers it by ownership and visibility, not
 * by guessing at page state.
 *
 * Rejections split two ways, and the line matters:
 *
 *   SILENT (never an order attempt in the first place, so a receipt line
 *   would be noise that trains the trader to ignore the strip): modifier
 *   chords including Shift, IME composition, key repeat, an open MODAL, a
 *   backgrounded tab, no focused card, not armed — and an owned keystroke
 *   while NOT armed-and-focused.
 *
 *   TRUTHFUL ACK (armed, deliberate, and refused — a silent drop here would
 *   leave the trader unable to tell "refused" from "sent"): the focused
 *   market is gone, the price row is not visible, the focused card is
 *   EXPANDED and therefore paints no price (#647 moneyline), no displayed
 *   price on that side, the line is not 'active', an earlier submission is
 *   unresolved — and an owned keystroke WHILE armed with a card focused,
 *   which is the one silence that misleads rather than merely declines.
 *
 * A refusal must name the state the trader is actually in, because the ack
 * doubles as the instruction for getting out of it. "Not visible" and
 * "expanded" both produce no usable price row, but their fixes are opposite:
 * scroll to the card, versus collapse the card sitting in plain sight. See
 * `expandedSuppressesPrice`.
 *
 * Only the "market is gone" case CLEARS the focus — the card no longer
 * exists. A merely off-screen card KEEPS its focus, so scrolling back
 * restores it without a re-click; so does an expanded one, so collapsing
 * restores it without a re-click either.
 *
 * Overlay handling is deliberately two-part, because "is this keystroke
 * mine?" and "is the card covered?" are different questions. Ownership walks
 * up from the event target, which catches everything a trader can type into
 * or press — including the interiors of portalled menus and popovers.
 * Modality is the only PAGE-level fact worth a global query: a modal can be
 * open with focus still on <body> (nothing inside it clicked yet), and it
 * physically covers the board. Non-modal popovers occlude nothing and must
 * NOT suppress — the pre-#609 bare `[role="dialog"]` query killed A/D
 * page-wide whenever any ⓘ InfoTooltip (cardKit) or the AlertControlsMenu
 * (aria-modal="false") was open, while missing real modals that carry no
 * role.
 *
 * The focused card is located by the marks its own rows paint —
 * `data-lt2-focused="<cardId>"` and `data-lt2-price` (see `focusedPriceRow`).
 * That coupling is deliberate: it keeps the focus store's signature free of
 * DOM, and it is pinned by SpreadsTotalsTable.lt2Focus.test.tsx so a row
 * refactor fails a test rather than silently disarming the fence. Every
 * unknown resolves to REFUSE — no rows, a row claiming another card, no or
 * multiple price rows, a shadow root we cannot see into, or an engine without
 * `checkVisibility`. "We could not tell" must never read as "it is visible".
 *
 * S, Q, Z, X are DELIBERATELY not bound (S stays unassigned; Q/Z/X remain
 * future ideas, out of scope). SPACEBAR is the V3 focused-game Pull All:
 * opt-in via its own setting, consumed only while armed + aimed, and always
 * an entry point into the exact-game cancel machinery (lib/trading/pullAll)
 * — authoritative order state, fail-closed selection, frozen snapshot,
 * fenced per-order cancels — never a DOM sweep. W and E are the Live Trading V3 ATTACK keys:
 * the bounded aggressive siblings of A and D. W acts on the same slot A
 * posts on (top/left), E on D's slot (bottom/right) — but instead of joining
 * the displayed top-of-book, an attack submits ONE bounded limit at the
 * level ABOVE the displayed best same-side bid (take it when opposing
 * liquidity is displayed there, establish it when not), at the separate
 * attack size, postOnly false. Every fence below applies to W/E exactly as
 * it does to A/D, plus: the trader must hold the separate attack grant
 * (silent otherwise — the keys don't exist for them), and Attack must be
 * armed on top of one-click (truthful refusal otherwise). The relay
 * re-checks grant, ceiling, and depth on every attack order.
 */
import { useEffect } from 'react';
import { useOneClickStore } from '@/stores/oneClickStore';
import { useAttackStore } from '@/stores/attackStore';
import { useAuthStore } from '@/stores/authStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useLt2FocusStore, type Lt2HotkeyTarget } from '@/stores/lt2FocusStore';
import { executeFocusedGamePull, isPullAllPending } from '@/lib/trading/pullAll';
import type { LiveOrderIntent } from '@/lib/trading/liveIntent';
import { LINE_AVAILABILITY_LABEL } from '@/lib/sport/lineAvailability';
import { probeMark, probeStart } from '@/lib/trading/orderLatencyProbe';
import {
  LIVE_HOTKEY_REGISTRY,
  actionForKey,
  type LiveHotkeyGrants,
  hotkeyDisplayLabel,
  hotkeyPairLabel,
  normalizeHotkey,
} from '@/lib/trading/liveHotkeyRegistry';
import { readOwnedHotkeys } from '@/hooks/useLiveHotkeys';

let _inFlight = false;

/**
 * Anything a keystroke can be "owned by": a surface that CONSUMES letter keys.
 * Matched with `closest()`, so a key pressed on a menu item inside a portalled
 * menu is owned by that menu even though the menu is nowhere near the card in
 * the DOM.
 *
 * Bare buttons are owners by default. The only exception is a button inside a
 * `data-lt2-hotkey-control` boundary: the explicit LT2 arm, line, perspective,
 * and price controls. Those controls deliberately hand the next A/D press back
 * to the focused trading card. Everything else — toolbar buttons, settings,
 * unrelated menus — refuses while focused, as #609 requires.
 *
 * `data-lt2-owner` opts a single button back OUT of that exemption. The
 * boundary is painted on a whole focused row, so it also covers controls that
 * merely live on the row without being LT2's — #602's fee-uncertainty help
 * button sits on the price pill itself, inside the boundary, and a request for
 * an explanation must never hand A/D back to the card. This is the same defect
 * #650 fenced on the resting-order cancel chip: a control that becomes
 * focusable becomes an A/D launch site. The chip could be fixed structurally
 * (it renders in its own row, outside the boundary); a marker drawn ON the
 * price cannot be, so it says so instead.
 *
 * `[role="dialog"]` appears here (interiors) but NOT in MODAL_SELECTOR below
 * (page-level suppression) — a non-modal popover owns its own keystrokes
 * without disabling the board behind it.
 */
const KEY_CONSUMING_OWNER_SELECTOR = [
  'input',
  'textarea',
  'select',
  'dialog',
  '[popover]',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="dialog"]',
].join(',');

const LT2_HOTKEY_CONTROL_SELECTOR = '[data-lt2-hotkey-control]';

/** Per-button opt-out from the boundary exemption above (#602). */
const LT2_OWNER_ATTRIBUTE = 'data-lt2-owner';

/**
 * Overlays that COVER the board, and so must suppress hotkeys page-wide even
 * when focus sits on <body>. `data-modal-root` is atoms/Modal's marker — the
 * single modal every LT2-reachable surface renders through; the aria pair is
 * the standards-based equivalent for anything that grows its own.
 */
const MODAL_SELECTOR = '[data-modal-root],[role="dialog"][aria-modal="true"],dialog[open]';

function ownedByInteractive(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  // A shadow root hides its interior from closest(): `e.target` retargets to
  // the HOST and `activeElement` reports the host, so an <input> inside a
  // password manager's or an editor extension's overlay reads as ordinary page
  // background and its keystrokes would place orders. We cannot see in, so we
  // refuse. A custom-element tag (the dash is the spec's own marker) is treated
  // the same even with a closed root, where `shadowRoot` reads null.
  if (el.shadowRoot != null || el.tagName.includes('-')) return true;
  if (el.closest(KEY_CONSUMING_OWNER_SELECTOR) != null) return true;

  const button = el.closest('button');
  if (button == null) return false;
  if (button.hasAttribute(LT2_OWNER_ATTRIBUTE)) return true;
  return button.closest(LT2_HOTKEY_CONTROL_SELECTOR) == null;
}

/** Ownership across every reachable view of the event's origin. */
function eventIsOwned(e: KeyboardEvent): boolean {
  // composedPath()[0] is the real innermost target for OPEN shadow roots,
  // where `e.target` has already retargeted to the host.
  const innermost = e.composedPath?.()[0] ?? e.target;
  return (
    ownedByInteractive(innermost) ||
    ownedByInteractive(e.target) ||
    ownedByInteractive(document.activeElement)
  );
}

/**
 * The row DISPLAYING the price A/D would submit, or null when we cannot say
 * which row that is.
 *
 * A focused card is one or two rows: the action row always, plus — when the
 * card is expanded — the ladder row beneath it. Which one bears the price
 * differs, and that is the whole point of this lookup: expanded, the resolver
 * reads the ladder's join bid (SpreadsTotalsTable `lt2TargetsFor`), which is
 * painted BELOW the action row and can sit under the fold while the action row
 * is comfortably on screen. Checking the action row there would certify a price
 * the trader cannot see — the exact clause this guard exists to enforce.
 *
 * So the rows carry three marks: `data-lt2-focused="<cardId>"` says "part of
 * the focused card", `data-lt2-price` says "this row shows the submittable
 * price", and `data-lt2-price-suppressed` says "this row is deliberately
 * unpriced because it is expanded" (read only by `expandedSuppressesPrice`,
 * to pick the ack wording — never to permit a fire).
 * Every unknown refuses: no rows, a row claiming a different card (stale paint
 * or two cards claiming focus — ambiguous focus is a documented pilot stop
 * condition), or anything other than exactly one price row.
 */
function focusedPriceRow(cardId: string, key: 'a' | 'd'): Element | null {
  const rows = [...document.querySelectorAll('[data-lt2-focused]')];
  if (rows.length === 0) return null;
  if (rows.some((r) => r.getAttribute('data-lt2-focused') !== cardId)) return null;
  const priced = rows.filter((r) => r.hasAttribute('data-lt2-price'));
  // One priced row serves BOTH keys — the derivative (spread/total) shape,
  // where a single row displays the whole two-action pair. Unchanged.
  if (priced.length === 1) return priced[0];
  // Two priced rows are the game-level moneyline shape: the top/away row is
  // marked "a" and the bottom/home row "d", and the pressed key must prove
  // ITS OWN team's row — A cannot certify the top team's price by finding
  // the bottom team's row on screen. Anything else is ambiguous and refuses.
  if (priced.length === 2) {
    const keyed = priced.filter((r) => r.getAttribute('data-lt2-price') === key);
    return keyed.length === 1 ? keyed[0] : null;
  }
  return null;
}

/** The card exists and is painted, but no single row shows a submittable
 *  price. Two different states produce that, and the trader needs to be told
 *  which one — the ack is the only instruction they get. */
const ACK_NOT_VISIBLE = "Focused card isn't visible — scroll to it or refocus";
const ACK_EXPANDED = 'Card is expanded — collapse it, then retry';

/**
 * A surface may deliberately suppress its own `data-lt2-price` mark while a
 * card is expanded, when the expanded presentation has no truthful two-action
 * A/D reading. The refusal is correct and the focus is kept; this helper only
 * fixes the wording, since such a card is sitting in front of the trader
 * rather than off-screen. (No current surface paints the mark — moneyline
 * keeps its price line visible through expansion, and the spread/total ladder
 * carries a real price row — but the fence stays: any future suppressing
 * surface gets the honest ack, never a fire.)
 *
 * The suppressing row must itself be ON SCREEN to earn the "collapse it" ack:
 * a card that is both expanded and scrolled away is genuinely not visible, and
 * telling the trader to collapse something they cannot see is the same defect
 * in the other direction. Ambiguity refuses to the default the way
 * `focusedPriceRow` does — this only ever changes which true sentence we say.
 */
function expandedSuppressesPrice(cardId: string): boolean {
  const rows = [...document.querySelectorAll('[data-lt2-focused]')];
  if (rows.length === 0) return false;
  if (rows.some((r) => r.getAttribute('data-lt2-focused') !== cardId)) return false;
  const suppressed = rows.filter((r) => r.hasAttribute('data-lt2-price-suppressed'));
  return suppressed.length === 1 && cardIsVisible(suppressed[0]);
}

/** Rendered, not hidden by CSS, and actually inside the viewport. */
function cardIsVisible(node: Element): boolean {
  // Fail closed on an engine that cannot answer, rather than skipping the
  // check — an unanswerable question is not a yes.
  if (typeof node.checkVisibility !== 'function') return false;
  if (!node.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
  const rect = node.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  return (
    rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth
  );
}

/** Exported for tests. */
export function handleLt2Keydown(e: KeyboardEvent): void {
  // The parity anchor: everything between this stamp and the local ack is
  // what a trader perceives as "hotkey lag" (see lib/trading/lt2Timing).
  const inputAtMs = Date.now();
  // #725 — WHICH key fires WHICH action is the trader's setting; what each
  // action then does, and every gate it must clear, is unchanged. `normalize`
  // folds letter case (the #609 Shift fold) and both spellings of the space
  // bar. An unmapped key resolves to null and is ordinary typing.
  const key = normalizeHotkey(e.key);
  // Ownership-gated: a mapping belonging to a previously signed-in trader must
  // never route this keystroke. Falls back to the shipped defaults.
  const hotkeys = readOwnedHotkeys();
  // #725 review 2 — the trader's grants decide which actions exist at all, so
  // they are resolved BEFORE the key is matched. An action the trader has no
  // grant for is not in the binding table, so its key is never consumed here
  // and never reserved in Settings: the two halves of "ungranted actions do
  // not consume their mapped key" now come from the same place.
  const grants: LiveHotkeyGrants = {
    attack: useAuthStore.getState().user?.attackAccess ?? false,
  };
  const action = actionForKey(hotkeys, key, grants);
  const isAttackKey = action === 'attack-top' || action === 'attack-bottom';
  const isPullKey = action === 'pull-all';
  // ⚠️ The KEY, not the action. Before #725 the spacebar could only ever be
  // Pull All, so "is this the pull action?" and "is this the spacebar?" were
  // the same question and the browser-default handling below keyed on the
  // former. Remapping split them: bind a POST to the spacebar and every
  // guard that asks about the action gets the wrong answer about the browser.
  const isSpaceKey = key === ' ';
  // Escape is NOT remappable: it is the app-wide dismissal contract
  // (useDismissibleLayer) and #648 fixes it as the focus clear. It is matched
  // separately so no remap can take it and none can shadow it.
  if (action == null && key !== 'Escape') return;
  // Mid-composition the keystroke belongs to the IME, including Escape (which
  // cancels the composition). `keyCode === 229` is the pre-isComposing signal
  // some IMEs still send.
  if (e.isComposing || e.keyCode === 229) return;

  const owned = eventIsOwned(e);
  const focus = useLt2FocusStore.getState();

  if (key === 'Escape') {
    if (owned) return; // Escape belongs to whatever the trader is in
    if (focus.focusedCardId != null) focus.clearFocus();
    return;
  }
  // Escape was the only unmapped key with a job. Everything past here needs a
  // mapped action, so an unbound key stays ordinary typing.
  if (action == null) return;

  const oneClick = useOneClickStore.getState();
  if (!oneClick.armed) return;
  const cardId = focus.focusedCardId;
  if (cardId == null) return; // no focus → no-op, silently
  // Spacebar consumes only when its own opt-in setting is on; otherwise it
  // stays exactly the browser key it always was (scroll, button activation).
  const pullEnabled = isPullKey && useDisplaySettings.getState().lt2PullAllEnabled;
  if (isPullKey && !pullEnabled) return;
  if (e.repeat) {
    // Held key must not machine-gun. A held SPACE additionally must not
    // start scrolling the board mid-hold after its first press was consumed —
    // true for ANY action bound to it, not just Pull All.
    if (isSpaceKey && !owned) e.preventDefault();
    return;
  }
  if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;

  // Attack exists only for attack-entitled traders. That is now enforced one
  // step earlier — `actionForKey` cannot return an attack action without the
  // grant — so for everyone else the key stays exactly what it was before V3:
  // ordinary typing, never a dead-key ack for a capability they do not have.
  // Kept as a second, local assertion because this is the branch that submits
  // an aggressive order, and a grant check is not something to hold in one
  // place only.
  if (isAttackKey && !grants.attack) return;

  const keyLabel = hotkeyDisplayLabel(key);
  // M3b latency probe: compare the passive top/bottom hotkey path against
  // mouse entry on the same marks. Attack and Pull All have separate timing
  // contracts and must not leave this passive-only probe open.
  if (action === 'passive-top' || action === 'passive-bottom') {
    probeStart(
      action === 'passive-top' ? 'hotkey-a' : 'hotkey-d',
      `Hotkey ${keyLabel}`,
    );
  }
  // Armed, focused, deliberate — and still refused. Silence here is the one
  // silence that misleads: the trader is mid-flow and cannot tell a refusal
  // from a sent order. Everywhere else an owned keystroke is simply not an
  // order attempt and says nothing.
  if (owned) {
    // A space typed into an input (or activating a focused button) is
    // ordinary browser behavior, never an order gesture — fully silent.
    // Keyed on the SPACEBAR, not on Pull All: a trader who binds a post to
    // space would otherwise get a spurious refusal every time they typed a
    // space into a search box.
    if (isSpaceKey) return;
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      'Focus is on another control — click the card, then retry',
    );
    return;
  }
  if (document.querySelector(MODAL_SELECTOR) != null) return;
  // Nobody is looking at a backgrounded tab, so nothing there is deliberate.
  if (document.visibilityState !== 'visible') return;

  // From here the press is CONSUMED: armed, aimed, un-owned, no modal, visible
  // tab. The browser must not ALSO act on it — even when the action then
  // refuses, because the refusal is the response.
  //
  // This used to live inside the Pull All branch alone, which was correct only
  // while the spacebar was Pull All's exclusive key. A post bound to the
  // spacebar scrolled the board on every truthful refusal, and punctuation
  // keys carry their own browser defaults (`/` opens quick-find in Firefox).
  // Every silent guard has already returned above, so nothing that was
  // deliberately left as ordinary typing is affected.
  e.preventDefault();

  if (isPullKey) {
    const targets = focus.resolveFocused();
    if (!targets) {
      oneClick.rejectLocal('Pull All', 'Focused market is gone — refocus a card');
      focus.clearFocus();
      return;
    }
    const scope = targets.pullScope;
    if (scope == null) {
      oneClick.rejectLocal('Pull All', 'This card cannot anchor a game-scoped pull');
      return;
    }
    if (isPullAllPending()) {
      oneClick.rejectLocal('Pull All', 'Pull All already pending — wait for the result');
      return;
    }
    oneClick.noteAck('pending', 'Pull All', `cancelling resting orders on ${targets.cardLabel}…`);
    void executeFocusedGamePull(scope.game).then(
      (outcome) => {
        oneClick.noteAck(
          outcome.status === 'refused' ? 'rejected' : 'accepted',
          'Pull All',
          outcome.message,
        );
      },
      (err: unknown) => {
        oneClick.noteAck(
          'rejected',
          'Pull All',
          err instanceof Error && err.message ? err.message : 'Pull failed — check open orders',
        );
      },
    );
    return;
  }

  // Armed, focused, entitled — but Attack itself is disarmed. This is the
  // deliberate W/E press the packet's first acceptance test names: visible
  // blocked/disarmed feedback, never a silent drop and never an order.
  if (isAttackKey && !useAttackStore.getState().attackArmed) {
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      `Attack is disarmed — arm Attack to use ${hotkeyPairLabel(
        hotkeys,
        'attack-top',
        'attack-bottom',
        grants,
      )}`,
    );
    return;
  }

  const targets = focus.resolveFocused();
  if (!targets) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'Focused market is gone — refocus a card');
    focus.clearFocus();
    return;
  }
  // The card still exists, but the row showing the price must be on screen to
  // be fired at. Focus is KEPT either way — scrolling back or collapsing is
  // the fix, not a re-click.
  // W shares A's slot (top/left), E shares D's (bottom/right): the attack
  // fires on the same book/side whose displayed row the fence must prove.
  // The slot is a property of the ACTION, so a remap cannot move an order to
  // a side other than the one whose displayed price the fence just proved.
  // FAIL-CLOSED, and deliberately not `?? 'a'`. An action with no slot is not
  // a single-sided order — Pull All today, the two-sided S action (#724) next.
  // Defaulting a missing slot to the top side would turn a press meant for
  // BOTH sides into one silent top-side order on a live venue. Nothing here
  // may guess which side the trader meant.
  const actionSlot = LIVE_HOTKEY_REGISTRY[action].slot;
  if (actionSlot == null) return;
  const slot: 'a' | 'd' = actionSlot;
  const priceRow = focusedPriceRow(cardId, slot);
  if (priceRow == null || !cardIsVisible(priceRow)) {
    // A row that exists but is off-screen is never "expanded" for ack
    // purposes — the price it would fire at is the one the trader can't see.
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      priceRow == null && expandedSuppressesPrice(cardId) ? ACK_EXPANDED : ACK_NOT_VISIBLE,
    );
    return;
  }
  const target: Lt2HotkeyTarget | null = slot === 'a' ? targets.a : targets.d;
  if (!target || target.priceCents == null) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'No displayed price on that side');
    return;
  }
  if (target.availability !== 'active') {
    oneClick.rejectLocal(
      target.label,
      `${LINE_AVAILABILITY_LABEL[target.availability]} — order not sent`,
    );
    return;
  }
  if (_inFlight) {
    oneClick.rejectLocal(target.label, 'Previous hotkey order still unresolved');
    return;
  }

  probeMark('fence-pass');
  // The durable `__lt2` audit stamp is a property of the ACTION, never of the
  // key: `hotkey-a` keeps meaning "the passive top/left action" for every order
  // ever placed, so remapping cannot rewrite what past records say they were
  // placed by. An action carrying no stamp places no order and must not reach
  // the intent builders below.
  const inputMethod = LIVE_HOTKEY_REGISTRY[action].inputMethod;
  if (inputMethod == null) return;

  if (isAttackKey) {
    // The resolver computed the bounded target from the displayed book (best
    // same-side bid + accepted depth). Null means there is nothing truthful
    // to attack: no displayed bid, a fee-hidden price, or a target outside
    // 1..99 — refuse with the reason, exactly like a missing passive price.
    const attack = target.attack;
    if (attack == null) {
      oneClick.rejectLocal(
        `Hotkey ${keyLabel}`,
        'No attackable level — needs a displayed bid with the level above in range',
      );
      return;
    }
    const labelBase = target.label.replace(/ @ \d+¢$/, '');
    const intent: LiveOrderIntent = {
      ticker: target.book?.marketTicker ?? '',
      side: target.side,
      priceCents: attack.targetPriceCents,
      postOnly: false,
      label: `ATT ${labelBase} @ ${attack.targetPriceCents}¢`,
      inputMethod,
      surface: targets.surface,
      marketKind: targets.marketKind,
      period: targets.period,
      line: targets.line,
      focusedCardId: focus.focusedCardId,
      displayedPriceMs: Date.now(),
      inputAtMs,
      attack: { depth: attack.depth, referencePriceCents: attack.referencePriceCents },
    };
    _inFlight = true;
    void oneClick
      .submitAttackIntent(intent, target.book)
      .finally(() => {
        _inFlight = false;
      });
    return;
  }

  const intent: LiveOrderIntent = {
    ticker: target.book?.marketTicker ?? '',
    side: target.side,
    priceCents: target.priceCents,
    postOnly: target.postOnly,
    label: target.label,
    inputMethod,
    surface: targets.surface,
    marketKind: targets.marketKind,
    period: targets.period,
    line: targets.line,
    focusedCardId: focus.focusedCardId,
    displayedPriceMs: Date.now(),
    inputAtMs,
  };
  _inFlight = true;
  void oneClick
    .submitIntent(intent, target.book)
    .finally(() => {
      _inFlight = false;
    });
}

export function Lt2HotkeyListener() {
  useEffect(() => {
    // `handleLt2Keydown` MUST stay a stable module-level reference. Both
    // hosting surfaces mount this component, and addEventListener dedupes on
    // (type, listener) — so even if they ever mounted together there is one
    // registration and one submission per press. Rewriting this as an inline
    // arrow looks harmless and would quietly create a double-registration
    // (hence double-submit) hazard. The surfaces are exclusive routes today,
    // which is also why the shared cleanup below cannot strand the survivor.
    window.addEventListener('keydown', handleLt2Keydown);
    // Disarming clears the focus: an unfocused, disarmed page is the only
    // safe idle state, and the ring must never suggest A/D are live.
    const unsubscribe = useOneClickStore.subscribe((state, prev) => {
      if (prev.armed && !state.armed) useLt2FocusStore.getState().clearFocus();
    });
    return () => {
      window.removeEventListener('keydown', handleLt2Keydown);
      unsubscribe();
      useLt2FocusStore.getState().clearFocus();
    };
  }, []);
  return null;
}
